import {
  faArrowAltCircleLeft,
  faEyeDropper,
  faSync,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ColorPicker from "../../../Component/ColorPicker/ColorPicker";
import Modal from "../../../Component/Modal/Modal";
import IframeCodeUbereats from "./IframeCodeUbereats";

const CreateWidgetUbereats = ({ userId }) => {
  const user = useSelector((state) => state.userReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [isScraping, setIsScraping] = useState(false);
  const [widgetId, setWidgetId] = useState("");

  const [widgetData, setWidgetData] = useState({
    userId,
    title: "",
    ubereatsUrl: "",
    storeName: "",
    storeImage: "",
    isOnline: "online",
    type: "Widget Ubereats",
    btnColor: "",
    backgroundColor: "",
    labelColor: "",
    borderColor: "",
    contactMail: "",
    contactPhone: "",
  });
  const [categories, setCategories] = useState([]);
  const [hasScraped, setHasScraped] = useState(false);

  const [activeColorPicker, setActiveColorPicker] = useState(null);
  const [colorPickers, setColorPickers] = useState({
    backgroundColor: false,
    btnColor: false,
    labelColor: false,
    borderColor: false,
  });

  const handleInputChange = (e) => {
    setWidgetData({ ...widgetData, [e.target.name]: e.target.value });
  };

  const handleColorPickerToggle = (fieldName) => {
    setColorPickers((prev) => ({
      ...Object.keys(prev).reduce((acc, key) => {
        acc[key] = key === fieldName ? !prev[key] : false;
        return acc;
      }, {}),
    }));
    setActiveColorPicker(fieldName);
  };

  const handleColorChange = (color) => {
    setWidgetData((prev) => ({ ...prev, [activeColorPicker]: color.hex }));
  };

  /* ── Scraping ── */
  const fetchProducts = async () => {
    if (!widgetData.ubereatsUrl) {
      toast.error("Collez d'abord l'URL Uber Eats du restaurant.");
      return;
    }
    setIsScraping(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/scrape`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url: widgetData.ubereatsUrl }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        setWidgetData((prev) => ({
          ...prev,
          ubereatsUrl: data.storeUrl || prev.ubereatsUrl,
          storeName: data.storeName || prev.storeName,
          storeImage: data.storeImage || prev.storeImage,
          title: prev.title || data.storeName || "",
        }));
        setCategories(data.categories || []);
        setHasScraped(true);
        const itemCount = (data.categories || []).reduce(
          (acc, c) => acc + (c.items?.length || 0),
          0
        );
        toast.success(`${itemCount} produits récupérés dans ${data.categories?.length || 0} catégories.`);
      } else {
        toast.error(data.error || "Récupération impossible.");
      }
    } catch (e) {
      toast.error("Erreur réseau lors de la récupération.");
    } finally {
      setIsScraping(false);
    }
  };

  /* ── Category / item helpers ── */
  const addCategory = () => {
    setCategories([
      ...categories,
      { name: "", order: categories.length, items: [] },
    ]);
  };

  const removeCategory = (catIdx) => {
    setCategories(categories.filter((_, i) => i !== catIdx));
  };

  const updateCategoryName = (catIdx, name) => {
    setCategories(categories.map((cat, i) => (i === catIdx ? { ...cat, name } : cat)));
  };

  const addItem = (catIdx) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              items: [
                ...(cat.items || []),
                { name: "", description: "", price: 0, imageUrl: "", productUrl: widgetData.ubereatsUrl, order: (cat.items || []).length },
              ],
            }
          : cat
      )
    );
  };

  const removeItem = (catIdx, itemIdx) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? { ...cat, items: cat.items.filter((_, j) => j !== itemIdx) }
          : cat
      )
    );
  };

  const updateItem = (catIdx, itemIdx, field, value) => {
    setCategories(
      categories.map((cat, i) =>
        i === catIdx
          ? {
              ...cat,
              items: cat.items.map((it, j) =>
                j === itemIdx ? { ...it, [field]: value } : it
              ),
            }
          : cat
      )
    );
  };

  /* ── Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasScraped && categories.length === 0) {
      toast.error("Récupérez d'abord les produits depuis l'URL Uber Eats.");
      return;
    }
    setIsLoading(true);

    const categoriesWithOrder = categories.map((cat, catIdx) => ({
      ...cat,
      order: catIdx,
      items: (cat.items || []).map((it, itemIdx) => ({ ...it, order: itemIdx })),
    }));

    const payload = { ...widgetData, categories: categoriesWithOrder };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/widgets/ubereats/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success("Widget créé avec succès");
        setWidgetId(data.widget._id);
      } else {
        const data = await response.json().catch(() => ({}));
        switch (response.status) {
          case 401:
            toast.error("Vous n'êtes pas connecté");
            break;
          case 400:
            toast.error(data.error || "Données invalides");
            break;
          default:
            toast.error("Une erreur est survenue");
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Erreur réseau");
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass =
    "text-center block w-full rounded-lg border border-gray-200 p-2.5 text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all";

  const totalItems = categories.reduce((acc, c) => acc + (c.items?.length || 0), 0);

  return (
    <div className="p-4 w-full">
      <Link to={`/`}>
        <button className="bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 my-1 w-[200px] flex justify-center items-center gap-2 text-sm font-medium transition-colors">
          <FontAwesomeIcon icon={faArrowAltCircleLeft} />
          <p>Revenir à la liste</p>
        </button>
      </Link>

      {user.user.isPremium ? (
        <div className="max-w-2xl mx-auto mt-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">

            {/* ── Section 1 : Source Uber Eats ── */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Source Uber Eats
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  URL de la page Uber Eats *
                </label>
                <input
                  className={inputClass}
                  type="url"
                  name="ubereatsUrl"
                  placeholder="https://www.ubereats.com/fr/store/..."
                  value={widgetData.ubereatsUrl}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <button
                type="button"
                onClick={fetchProducts}
                disabled={isScraping || !widgetData.ubereatsUrl}
                className="w-full p-3 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={isScraping ? faSpinner : faSync} spin={isScraping} />
                {isScraping ? "Récupération en cours..." : "Récupérer les produits"}
              </button>
              {hasScraped && (
                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2.5">
                  <strong>{totalItems} produits</strong> récupérés dans {categories.length} catégorie{categories.length > 1 ? "s" : ""}. Vous pouvez maintenant les éditer ci-dessous.
                </p>
              )}
            </div>

            {/* ── Section 2 : Informations générales ── */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Informations générales
              </p>
              <div>
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Titre du widget *
                </label>
                <input
                  className={inputClass}
                  type="text"
                  name="title"
                  placeholder="Ex : Menu Basta Basta — Bastia"
                  value={widgetData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">
                  Statut
                </label>
                <select
                  className={inputClass}
                  name="isOnline"
                  value={widgetData.isOnline}
                  onChange={handleInputChange}
                >
                  <option value="online">En ligne</option>
                  <option value="offline">Hors ligne</option>
                </select>
              </div>
            </div>

            {/* ── Section 3 : Catalogue éditable ── */}
            {categories.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Catalogue ({totalItems} produits)
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <strong className="text-amber-900">Modifiez librement le contenu.</strong>{" "}
                    Vos modifications seront sauvegardées avec le widget. Le bouton "Commander" redirige vers la fiche produit Uber Eats.
                  </p>
                </div>

                {categories.map((cat, catIdx) => (
                  <div key={catIdx} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
                    <div className="flex items-center gap-2">
                      <input
                        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                        type="text"
                        placeholder="Nom de la catégorie"
                        value={cat.name}
                        onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                      />
                      <button
                        type="button"
                        className="w-8 h-8 shrink-0 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 flex items-center justify-center transition-colors"
                        onClick={() => removeCategory(catIdx)}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {(cat.items || []).map((item, itemIdx) => (
                      <div key={itemIdx} className="bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt=""
                              className="w-10 h-10 rounded-md object-cover shrink-0"
                            />
                          )}
                          <input
                            className="flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all"
                            type="text"
                            placeholder="Nom du produit"
                            value={item.name}
                            onChange={(e) => updateItem(catIdx, itemIdx, "name", e.target.value)}
                          />
                          <div className="relative shrink-0">
                            <input
                              className="w-[80px] rounded-md border border-gray-200 px-2 py-1.5 pr-5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all text-right"
                              type="number"
                              step="0.01"
                              placeholder="Prix"
                              value={item.price}
                              onChange={(e) => updateItem(catIdx, itemIdx, "price", Number(e.target.value))}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">€</span>
                          </div>
                          <button
                            type="button"
                            className="w-7 h-7 shrink-0 rounded-md bg-gray-100 text-gray-400 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                            onClick={() => removeItem(catIdx, itemIdx)}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <textarea
                          className="rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent bg-white transition-all resize-none"
                          rows="2"
                          placeholder="Description"
                          value={item.description || ""}
                          onChange={(e) => updateItem(catIdx, itemIdx, "description", e.target.value)}
                        />
                      </div>
                    ))}

                    <button
                      type="button"
                      className="w-full p-2 rounded-lg text-xs font-semibold border-2 border-dashed border-gray-200 text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-all"
                      onClick={() => addItem(catIdx)}
                    >
                      + Ajouter un produit
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="w-full p-3 rounded-xl text-sm font-semibold border-2 border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all"
                  onClick={addCategory}
                >
                  + Ajouter une catégorie
                </button>
              </div>
            )}

            {/* ── Section 4 : Apparence ── */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Apparence
              </p>
              <div className="-mx-3 flex flex-wrap">
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur de fond"
                    color={widgetData.backgroundColor}
                    onColorChange={handleColorChange}
                    isOpen={colorPickers.backgroundColor}
                    togglePicker={() => handleColorPickerToggle("backgroundColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur des boutons"
                    color={widgetData.btnColor}
                    onColorChange={handleColorChange}
                    isOpen={colorPickers.btnColor}
                    togglePicker={() => handleColorPickerToggle("btnColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur du texte"
                    color={widgetData.labelColor}
                    onColorChange={handleColorChange}
                    isOpen={colorPickers.labelColor}
                    togglePicker={() => handleColorPickerToggle("labelColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
              </div>
              <div className="-mx-3 flex flex-wrap mt-1">
                <div className="md:w-1/3 px-3 mb-3 md:mb-0">
                  <ColorPicker
                    label="Couleur de bordure"
                    color={widgetData.borderColor}
                    onColorChange={handleColorChange}
                    isOpen={colorPickers.borderColor}
                    togglePicker={() => handleColorPickerToggle("borderColor")}
                    placeholder={<FontAwesomeIcon icon={faEyeDropper} />}
                  />
                </div>
              </div>

              {/* Preview */}
              {(() => {
                const accent = widgetData.btnColor || "#e85a2a";
                const accentText = widgetData.labelColor || "#ffffff";
                const previewBg = widgetData.backgroundColor || "#ffffff";
                const previewFg = widgetData.labelColor || "#1a1a1a";
                const previewBrd =
                  widgetData.borderColor && widgetData.borderColor !== "transparent"
                    ? widgetData.borderColor
                    : null;
                return (
                  <div
                    className="rounded-xl p-5 mt-1"
                    style={{
                      backgroundColor: previewBg,
                      border: previewBrd ? `2px solid ${previewBrd}` : "1px solid #ececec",
                    }}
                  >
                    <p className="text-[9px] font-bold uppercase tracking-widest mb-3" style={{ color: previewFg, opacity: 0.4 }}>
                      Aperçu en temps réel
                    </p>
                    {/* Tab nav preview */}
                    <div className="flex gap-1 mb-4 border-b" style={{ borderColor: `${previewFg}10` }}>
                      <span
                        className="flex items-center gap-1.5 text-[12px] font-semibold pt-2 pb-2 px-2 relative"
                        style={{ color: accent }}
                      >
                        Burgers
                        <span
                          className="text-[9px] font-bold px-1.5 py-px rounded-full leading-tight"
                          style={{ backgroundColor: `${accent}1a`, color: accent, minWidth: "18px", textAlign: "center" }}
                        >9</span>
                        <span
                          className="absolute left-2 right-2 -bottom-px h-[2px] rounded"
                          style={{ background: `linear-gradient(to right, ${accent}, ${accent}dd)`, boxShadow: `0 -1px 6px ${accent}55` }}
                        />
                      </span>
                      <span
                        className="flex items-center gap-1.5 text-[12px] font-semibold pt-2 pb-2 px-2"
                        style={{ color: `${previewFg}60` }}
                      >
                        Boissons
                        <span
                          className="text-[9px] font-bold px-1.5 py-px rounded-full leading-tight"
                          style={{ backgroundColor: `${previewFg}10`, color: `${previewFg}80`, minWidth: "18px", textAlign: "center" }}
                        >7</span>
                      </span>
                    </div>
                    {/* Section header preview */}
                    <div className="flex items-center gap-3 mb-3">
                      <span
                        className="flex-1 h-px"
                        style={{ background: `linear-gradient(to right, transparent, ${accent}55, transparent)` }}
                      />
                      <h3 className="text-[12px] font-bold tracking-tight" style={{ color: previewFg }}>Burgers</h3>
                      <span
                        className="flex-1 h-px"
                        style={{ background: `linear-gradient(to right, transparent, ${accent}55, transparent)` }}
                      />
                    </div>
                    {/* Product card preview */}
                    <div
                      className="flex gap-3 p-3 rounded-2xl bg-white"
                      style={{ border: "1px solid #eeeeee", boxShadow: "0 2px 8px rgba(0,0,0,0.03)" }}
                    >
                      <div className="w-[68px] h-[68px] rounded-2xl bg-gradient-to-br from-orange-200 to-orange-400 shrink-0" />
                      <div className="flex-1 min-w-0 flex flex-col">
                        <p className="text-left text-[12px] font-bold text-neutral-900 leading-snug">Smash burger</p>
                        <p className="text-left text-[10px] text-neutral-500 leading-relaxed mt-0.5 line-clamp-2">
                          Steak haché smash, fromage fondant, oignons hachés
                        </p>
                        <div className="flex items-center justify-between mt-auto pt-1.5">
                          <p className="text-[13px] font-extrabold text-neutral-900 tracking-tight">
                            11,50<span className="text-[10px] font-bold text-neutral-400 ml-0.5">€</span>
                          </p>
                          <span
                            className="inline-flex items-center justify-center w-7 h-7 rounded-full"
                            style={{
                              backgroundColor: accent,
                              color: accentText,
                              boxShadow: `0 2px 8px ${accent}40`,
                            }}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ── Section 5 : Contact (optionnel) ── */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 flex flex-col gap-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                Contact (optionnel)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Email
                  </label>
                  <input
                    className={inputClass}
                    type="email"
                    name="contactMail"
                    placeholder="contact@restaurant.fr"
                    value={widgetData.contactMail}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">
                    Téléphone
                  </label>
                  <input
                    className={inputClass}
                    type="tel"
                    name="contactPhone"
                    placeholder="04 95 00 00 00"
                    value={widgetData.contactPhone}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !hasScraped}
              className="w-full p-3 rounded-xl text-sm font-semibold text-white bg-black hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLoading ? "Chargement..." : "Créer le widget"}
            </button>
          </form>
        </div>
      ) : null}

      {widgetId && (
        <Modal
          title="Code iframe"
          isOpen={true}
          onClose={() => setWidgetId("")}
          children={<IframeCodeUbereats widgetId={widgetId} />}
        />
      )}
    </div>
  );
};

export default CreateWidgetUbereats;
